import React from 'react';
import { useStudent } from '../context/StudentContext';
import { Calendar, CheckCircle, AlertCircle, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Dashboard.css';

const Dashboard = () => {
    const { student, stats, getNextLesson, financials, loading, error, refetch } = useStudent();
    console.log('Dashboard: Render states:', { loading, hasStudent: !!student, hasError: !!error });
    const nextLesson = getNextLesson();

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-slate-400 animate-pulse font-medium">Loading your profile...</p>
                </div>
            </div>
        );
    }

    if (!student || error) return (
        <div className="page-container flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
            <div className="max-w-md bg-slate-800/50 p-10 rounded-3xl border border-slate-700/50 backdrop-blur-xl">
                <div className="w-20 h-20 bg-[#ebc11f]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <AlertCircle size={40} className="text-[#ebc11f]" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">
                    {error ? 'System Error' : 'Account Not Linked'}
                </h2>
                <p className="text-slate-400 mb-8 leading-relaxed">
                    {error ? `An error occurred: ${error}` :
                        `We couldn't find a student record linked to your account (${window.reactPress?.user?.login || 'Unknown User'}).`}
                    <br /><br />
                    This normally happens if your registration hasn't been finalized.
                </p>
                <div className="space-y-4">
                    <button className="bg-[#ebc11f] text-black w-full py-4 rounded-xl font-bold hover:bg-[#d4ad1b] transition-colors">Contact Administrator</button>
                    <button onClick={() => window.location.reload()} className="border border-slate-600 text-white w-full py-4 rounded-xl font-bold hover:bg-slate-700 transition-colors">Refresh Dashboard</button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="page-container dashboard-page">
            <header className="dashboard-header">
                <div>
                    <h1 className="welcome-text">Welcome back, {student.name?.split(' ')[0] || 'Student'}!</h1>
                    <p className="subtitle">Track your progress and upcoming lessons</p>
                </div>
            </header>

            <div className="dashboard-grid">
                {/* Progress Card */}
                <div className="card progress-card">
                    <div className="card-header">
                        <h3>Current Progress</h3>
                        <span className="badge badge-primary">{student.stage}</span>
                    </div>
                    <div className="progress-stats">
                        <div className="stat">
                            <span className="label">Lessons Completed</span>
                            <span className="value">{stats.lessonsCompleted}/{stats.lessonsTotal}</span>
                        </div>
                        <div className="stat">
                            <span className="label">Hours Driven</span>
                            <span className="value">{stats.hoursCompleted} hrs</span>
                        </div>
                    </div>
                    <div className="progress-bar-container">
                        <div
                            className="progress-bar-fill"
                            style={{ width: `${stats.progress}%` }}
                        ></div>
                    </div>
                    <p className="progress-text">{stats.progress}% to completion</p>
                </div>

                {/* Next Lesson Card */}
                <div className="card next-lesson-card">
                    <div className="card-header">
                        <h3>Next Lesson</h3>
                        {nextLesson && <span className="badge badge-warning">Upcoming</span>}
                    </div>
                    {nextLesson ? (
                        <div className="lesson-details">
                            <div className="detail-row">
                                <Calendar size={20} className="icon-gold" />
                                <div>
                                    <p className="detail-label">Date & Time</p>
                                    <p className="detail-value">
                                        {new Date(nextLesson.date).toLocaleDateString()} at {new Date(nextLesson.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </div>
                            <div className="detail-row">
                                <CheckCircle size={20} className="icon-gold" />
                                <div>
                                    <p className="detail-label">Instructor</p>
                                    <p className="detail-value">{nextLesson.instructor}</p>
                                </div>
                            </div>
                            <Link to="/schedule" className="btn btn-outline full-width">View Full Schedule</Link>
                        </div>
                    ) : (
                        <div className="empty-state">
                            <p>No upcoming lessons scheduled.</p>
                            <button className="btn btn-primary">Book a Lesson</button>
                        </div>
                    )}
                </div>
            </div>

            {/* Quick Actions / Notifications */}
            <div className="quick-actions-grid">
                <Link to="/financials" className="action-card">
                    <div className="action-content">
                        <h3>My Account</h3>
                        <p>View invoices and receipts</p>
                    </div>
                    <ChevronRight size={24} />
                </Link>
            </div>
        </div>
    );
};

export default Dashboard;
